import type { Router, RouteLocationNormalized } from 'vue-router'
import { reportNetworkFailure, reportNetworkSuccess } from './networkHealth'
import { hasAppUpdateSubscribers } from './useAppUpdate'
import { withCacheBust } from './cacheBust'

/**
 * Recover from stale-bundle navigation failures after a deploy — and make sure
 * a failed recovery NEVER leaves the user staring at a blank white screen.
 *
 * Problem: every deploy changes the content-hash of lazily-loaded route
 * chunks (`() => import('...')`). A client whose SPA was loaded BEFORE the
 * deploy still references the old hashes. Because the app is already running
 * (especially inside the long-lived Bitrix24 iframe, where index.html is
 * never re-requested), the next lazy navigation fetches a chunk that no longer
 * exists on the server → 404. vue-router catches the dynamic-import rejection
 * in `router.onError` and **silently aborts the navigation** — to the user the
 * tab/link "doesn't react", or (if it happens during the FIRST mount) the boot
 * splash is cleared into an empty `#app` and the screen goes white.
 *
 * Fix: when we detect a chunk-load failure, do a full-page reload to the
 * intended destination. The reload re-fetches index.html (served `no-cache`),
 * which points at the fresh chunk hashes, and the navigation succeeds.
 *
 * Resilience: a single reload sometimes isn't enough inside the B24 desktop
 * webview (it can serve a cached index.html on the first reload). We allow a
 * few attempts within a short window; once they're exhausted we render a
 * VISIBLE "обновите страницу" fallback instead of silently giving up to a
 * white screen — so the user always has a way out.
 *
 * The same `handleChunkError` is exported for `app.config.errorHandler` and the
 * global `unhandledrejection`/`error` listeners, so a chunk failure that throws
 * during render (not just during navigation) is recovered too.
 */

const RELOAD_FLAG = 'fs:chunk-reload-attempt'
const RELOAD_COUNT = 'fs:chunk-reload-count'
const RELOAD_LOOP_WINDOW_MS = 30_000
const RELOAD_MAX_ATTEMPTS = 2

// Transport probe before reloading (TSPU/DPI filtering windows, 2026-07-02
// incident): a chunk can fail not because it's stale (deploy) but because the
// network is dropping our connections for a few MINUTES. Reloading into a dead
// window burns the recovery attempts for nothing and kills a working app. So:
// probe first; if the transport is dead, WAIT for it to come back (up to
// TRANSPORT_WAIT_MAX_MS) and only then reload.
const TRANSPORT_PROBE_TIMEOUT_MS = 5_000
const TRANSPORT_WAIT_STEP_MS = 5_000
const TRANSPORT_WAIT_MAX_MS = 90_000

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  const name = error instanceof Error ? error.name : ''

  return (
    name === 'ChunkLoadError' ||
    // Vite / Rollup dynamic-import failures (Chrome, Firefox)
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // Safari wording
    /Importing a module script failed/i.test(message) ||
    // Webpack-style wording (kept for safety)
    /Loading chunk \S+ failed/i.test(message) ||
    /Loading CSS chunk \S+ failed/i.test(message) ||
    // Stale index.html served in place of a removed .js chunk → wrong MIME
    /is not a valid JavaScript MIME type/i.test(message) ||
    /expected a JavaScript module script but the server responded/i.test(message)
  )
}

function attemptsInWindow(): number {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0)
    if (!last || Date.now() - last > RELOAD_LOOP_WINDOW_MS) return 0
    return Number(sessionStorage.getItem(RELOAD_COUNT) || 0)
  } catch {
    return 0
  }
}

function markReloaded(): void {
  try {
    const count = attemptsInWindow() + 1
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
    sessionStorage.setItem(RELOAD_COUNT, String(count))
  } catch {
    /* sessionStorage unavailable (private mode / iframe) — proceed anyway */
  }
}

function clearReloadState(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG)
    sessionStorage.removeItem(RELOAD_COUNT)
  } catch {
    /* ignore */
  }
}

/**
 * Last-resort visible fallback so an exhausted recovery never ends on a blank
 * white screen. Plain DOM (Vue may be unmounted or mid-failure), matches the
 * boot-splash look, gives the user an explicit "Обновить" button.
 */
function showReloadFallback(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById('em-chunk-fallback')) return

  const overlay = document.createElement('div')
  overlay.id = 'em-chunk-fallback'
  overlay.setAttribute('role', 'alert')
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:99999',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:16px',
    'padding:24px',
    'text-align:center',
    'background:#0b0b0f',
    'color:#f5f5f7',
    "font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  ].join(';')

  const title = document.createElement('p')
  title.textContent = 'Не удалось загрузить приложение'
  title.style.cssText = 'margin:0;font-size:18px;font-weight:600;'

  const hint = document.createElement('p')
  hint.textContent =
    'Проверьте подключение к интернету и обновите страницу. Если не поможет — подождите минуту и попробуйте ещё раз.'
  hint.style.cssText = 'margin:0;font-size:14px;opacity:0.7;max-width:320px;line-height:1.4;'

  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = 'Обновить'
  button.style.cssText = [
    'margin-top:8px',
    'padding:10px 24px',
    'font-size:15px',
    'font-weight:600',
    'color:#fff',
    'background:#7c5cff',
    'border:none',
    'border-radius:12px',
    'cursor:pointer',
  ].join(';')
  button.addEventListener('click', () => {
    clearReloadState()
    window.location.reload()
  })

  overlay.appendChild(title)
  overlay.appendChild(hint)
  overlay.appendChild(button)
  document.body.appendChild(overlay)
}

// Re-exported for existing callers/tests; the implementation moved to
// `cacheBust.ts` so `useAppUpdate` can use it without an import cycle.
export { withCacheBust }

/** Any HTTP response — even 404 — proves the pipe works; only a network-level
 *  rejection means the transport is dead. */
async function isTransportAlive(): Promise<boolean> {
  if (typeof fetch === 'undefined' || typeof window === 'undefined') return true
  const controller = new AbortController()
  // AbortSignal.timeout() needs Chrome 103+/Safari 16 — out of browserslist.
  const abortTimer = setTimeout(() => controller.abort(), TRANSPORT_PROBE_TIMEOUT_MS)
  try {
    await fetch(withCacheBust(window.location.origin + '/'), {
      cache: 'no-store',
      signal: controller.signal,
    })
    reportNetworkSuccess()
    return true
  } catch {
    reportNetworkFailure()
    return false
  } finally {
    clearTimeout(abortTimer)
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

let recovering = false

async function recover(targetHref?: string): Promise<void> {
  // A screenful of failed chunks fires several errors at once — one recovery
  // flow is enough.
  if (recovering) return
  recovering = true
  try {
    // Distinguish "chunk is stale" (deploy → reload fixes it) from "network is
    // dropping us" (TSPU window → reload would fail too). In the latter case
    // wait the window out, then reload once the transport answers again.
    // reportNetworkFailure() inside the probe also lights NetworkStatusBanner,
    // so the user sees WHY nothing happens instead of a frozen screen.
    if (!(await isTransportAlive())) {
      const deadline = Date.now() + TRANSPORT_WAIT_MAX_MS
      let alive = false
      while (Date.now() < deadline) {
        await sleep(TRANSPORT_WAIT_STEP_MS)
        if (await isTransportAlive()) {
          alive = true
          break
        }
      }
      if (!alive) {
        showReloadFallback()
        return
      }
    }

    if (attemptsInWindow() >= RELOAD_MAX_ATTEMPTS) {
      // Reloading isn't clearing it — surface a visible recovery UI instead of a
      // silent white screen.
      showReloadFallback()
      return
    }
    markReloaded()

    // Navigate (not reload()) to a cache-busted URL so even a cache-happy webview
    // re-fetches index.html with the fresh chunk hashes. `assign` to a URL that
    // differs only by the `_emr` param still triggers a full document load.
    const dest =
      targetHref ??
      window.location.pathname + window.location.search + window.location.hash
    window.location.assign(withCacheBust(dest))
  } finally {
    recovering = false
  }
}

/**
 * Handle a possible chunk-load error from anywhere (render errorHandler, global
 * unhandledrejection/error). Returns true if it WAS a chunk error and was
 * handled (so callers can stop further processing).
 */
export function handleChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false
  void recover()
  return true
}

/**
 * Install global chunk-load-error recovery on a vue-router instance.
 * Call once, after the router is created, in each SPA's `main.ts`.
 */
export function installChunkReloadGuard(router: Router): void {
  router.onError((error, to?: RouteLocationNormalized) => {
    if (!isChunkLoadError(error)) return
    // `to.fullPath` is base-relative; resolve() re-applies the router base
    // (e.g. app_org is served under `/admin/`) so the reload lands correctly.
    const href = to ? router.resolve(to).href : undefined
    void recover(href)
  })

  if (typeof window !== 'undefined') {
    // Vite fires this when a <link rel="modulepreload"> for a route chunk 404s,
    // which can happen before `router.onError` sees the rejection.
    window.addEventListener('vite:preloadError', (event) => {
      ;(event as Event & { preventDefault?: () => void }).preventDefault?.()

      // Если баннер обновления смонтирован (useAppUpdate), решение принимает
      // человек: он мог полчаса писать урок, и молчаливая перезагрузка стёрла
      // бы текст. Баннер уже поднят самим useAppUpdate — здесь просто не мешаем.
      if (hasAppUpdateSubscribers()) return

      // Баннера нет — например, идёт первая загрузка и экран ещё пустой.
      // Терять нечего, спасаем вкладку перезагрузкой, как и раньше.
      void recover()
    })

    // A chunk import can reject as an unhandled promise (e.g. during the very
    // first mount, before any navigation) — catch it so the boot doesn't end
    // on a blank screen.
    window.addEventListener('unhandledrejection', (event) => {
      if (handleChunkError((event as PromiseRejectionEvent).reason)) {
        ;(event as Event & { preventDefault?: () => void }).preventDefault?.()
      }
    })
  }
}
